import { motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import Navbar from "@/components/Navbar";
import Seo from "@/components/Seo";

const ACCENT = "#4FA3D9";

const TM = () => <sup className="ml-0.5 text-[0.5em] align-super text-white/45">™</sup>;

const Sec = ({ n, title, children }) => (
  <section className="mt-11">
    <h2 className="font-display text-xl font-medium tracking-tight text-white sm:text-2xl">
      {n}. {title}
    </h2>
    <div className="mt-4 space-y-4">{children}</div>
  </section>
);

const P = ({ children }) => <p className="text-base leading-relaxed text-white/65">{children}</p>;

const Bullets = ({ items }) => (
  <ul className="space-y-2.5">
    {items.map((it, i) => (
      <li key={i} className="flex gap-3 text-base leading-relaxed text-white/65">
        <span className="mt-[9px] h-1 w-1 flex-shrink-0 rounded-full" style={{ background: ACCENT }} />
        <span>{it}</span>
      </li>
    ))}
  </ul>
);

const Mail = () => (
  <a
    href="mailto:hello@adaptience.com"
    className="font-medium underline decoration-white/25 underline-offset-2 transition-colors hover:decoration-white/60"
    style={{ color: ACCENT }}
  >
    hello@adaptience.com
  </a>
);

export const PrivacyPolicy = ({ onBack }) => {
  return (
    <div className="relative h-[100dvh] w-full overflow-y-auto bg-[#0a0a0a] text-white" data-testid="privacy-page">
      <Seo
        title="Privacy Policy | Adaptience"
        description="How Adaptience handles data and protects privacy across its interactive life simulation platform."
        canonical="/privacy"
      />
      {/* subtle top glow */}
      <div className="pointer-events-none fixed inset-x-0 top-0 h-[60vh]">
        <div className="absolute left-1/2 top-[-10vh] h-[45vh] w-[70vw] -translate-x-1/2 rounded-full blur-[150px]" style={{ background: "rgba(79,163,217,0.09)" }} />
      </div>

      {/* NAV */}
      <Navbar />

      <main className="relative z-10 mx-auto w-full max-w-3xl px-6 py-16 sm:px-10 sm:py-24">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        >
          <p className="mb-3 text-xs font-semibold uppercase tracking-[0.28em]" style={{ color: ACCENT }}>
            Adaptience<TM />
          </p>
          <h1 className="font-display text-4xl font-semibold leading-[1.05] tracking-tight sm:text-5xl">
            Privacy Policy
          </h1>

          {/* ===================================================================
              PRIVACY POLICY CONTENT
          =================================================================== */}
          <div className="mt-10" data-testid="privacy-content">
            <p className="text-sm text-white/40">Last updated: August 2026</p>
            <div className="mt-6 space-y-4">
              <P>Adaptience™ respects the privacy of children, parents and guardians who use our website and simulations.</P>
              <P>This Privacy Policy explains what information may be collected when you use Adaptience™, why it is used, and the choices available to parents and guardians.</P>
            </div>

            <Sec n={1} title="About Adaptience™">
              <P>Adaptience™ is a simulation-based learning and practice platform designed to help people practise navigating difficult and uncertain real-life situations before encountering them in real life.</P>
              <P>We are currently beginning with simulations designed for children aged 10–14, with parent or guardian involvement.</P>
              <P>For privacy questions or requests, contact: <Mail /></P>
            </Sec>

            <Sec n={2} title="Parent or Guardian Involvement">
              <P>Our current children's simulations are intended to be accessed with the involvement and permission of a parent or legal guardian.</P>
              <P>We do not intentionally ask children to provide personal identifying information such as their full name, home address, telephone number, email address, school name or social-media account information while participating in a simulation.</P>
              <P>Children should not enter such information in their simulation responses.</P>
            </Sec>

            <Sec n={3} title="Information We May Process">
              <P>Depending on how you use Adaptience™, information processed may include:</P>
              <Bullets items={[
                "responses and choices made during a simulation;",
                "text generated from spoken responses, where voice input is used;",
                "AI-generated responses and simulation outcomes;",
                "information used to generate the Child Reflection and Parent Adaptience Practice Profile;",
                "simulation history where that functionality is enabled;",
                "feedback voluntarily provided after a simulation;",
                "parent or guardian email address;",
                "optional telephone or WhatsApp number where voluntarily provided;",
                "early-access preferences; and",
                "limited technical information necessary for operating, securing and improving the service.",
              ]} />
              <P>We aim to collect only information reasonably necessary to provide and improve the Adaptience™ experience.</P>
            </Sec>

            <Sec n={4} title="Voice Input">
              <P>Some simulations may allow a participant to respond using their voice.</P>
              <P>Voice input may be processed using speech-to-text technology so that the simulation can understand and respond to the participant.</P>
              <P>Adaptience™ is designed to use the resulting response for the simulation experience. We do not intentionally use children's voices for advertising or marketing.</P>
              <P>Parents and children may use text input instead where that option is available.</P>
            </Sec>

            <Sec n={5} title="How Simulation Information Is Used">
              <P>Responses during a simulation may be processed by artificial intelligence and other software systems to:</P>
              <Bullets items={[
                "understand the participant's response;",
                "determine how the simulated situation should continue;",
                "provide realistic and age-appropriate reactions;",
                "generate a reflection after the simulation;",
                "generate observations for the Parent Adaptience Practice Profile; and",
                "improve the safety, quality and usefulness of the experience.",
              ]} />
              <P>Adaptience™ Practice Profiles are based only on behaviour and choices within a simulated situation.</P>
              <P>They are not psychological assessments or diagnoses.</P>
            </Sec>

            <Sec n={6} title="Artificial Intelligence">
              <P>Adaptience™ uses artificial intelligence as part of its simulation experience.</P>
              <P>AI systems may process participant responses to understand what was said or typed, generate appropriate simulation reactions, and assist in producing post-simulation observations.</P>
              <P>AI-generated content may sometimes be incomplete, inaccurate or unexpected.</P>
              <P>We use controls intended to keep simulations within defined, age-appropriate boundaries, but parents and guardians should remain involved in their child's use of the service.</P>
            </Sec>

            <Sec n={7} title="Feedback and Contact Information">
              <P>After a simulation, you may voluntarily provide feedback. Feedback is optional and, unless you request early access to future simulations, can be submitted without providing an email address or phone number.</P>
              <P>Feedback may include:</P>
              <Bullets items={[
                "experience ratings;",
                "whether you would like more simulations;",
                "suggestions for future situations;",
                "written comments;",
                "an optional email address;",
                "an optional telephone or WhatsApp number; and",
                "whether you would like early access to future Adaptience™ simulations.",
              ]} />
              <P>If you select “I'd like early access to future Adaptience™ simulations”, you are asking us to contact you about early access to future simulations. To do this, we need an email address or a telephone/WhatsApp number. This is not consent to newsletters or general marketing, and we will not subscribe you to anything else.</P>
              <P>Providing feedback does not create an Adaptience™ account. Feedback contact information is kept separate from Contact form enquiries and from any email address used to send a Practice Report, and is not automatically combined with them.</P>
              <P>Feedback may be used to understand user needs, improve Adaptience™ and, where you have requested early access, to contact you about future simulations.</P>
            </Sec>

            <Sec n={8} title="Children's Personal Information">
              <P>Protecting children's privacy is particularly important to us.</P>
              <P>Adaptience™ does not require a child to disclose unnecessary personal identifying information in order to participate in the current simulation experience.</P>
              <P>Parents and guardians should instruct children not to enter information such as:</P>
              <Bullets items={[
                "full name;",
                "address;",
                "telephone number;",
                "personal email address;",
                "school name;",
                "passwords;",
                "social-media usernames; or",
                "other information that could directly identify them",
              ]} />
              <P>within free-text or spoken simulation responses.</P>
              <P>If we become aware that personal information from a child has been collected in a manner inconsistent with applicable requirements, we will take reasonable steps to address or delete it.</P>
            </Sec>

            <Sec n={9} title="Service Providers">
              <P>Adaptience™ relies on technology service providers to operate features such as hosting, databases, artificial intelligence, speech processing and other application functionality.</P>
              <P>These providers may process limited information as necessary to provide their services to Adaptience™.</P>
              <P>We do not sell children's personal information.</P>
            </Sec>

            <Sec n={10} title="Data Retention">
              <P>We aim to retain personal information only for as long as reasonably necessary for the purpose for which it was collected, including providing the service, improving the product, maintaining security, meeting legal obligations and resolving disputes.</P>
              <P>As Adaptience™ develops beyond its initial testing phase, our retention practices may evolve and this Privacy Policy will be updated accordingly.</P>
            </Sec>

            <Sec n={11} title="Parent and Guardian Choices">
              <P>A parent or guardian may contact us regarding personal information associated with their use of Adaptience™, including to request access, correction or deletion where applicable.</P>
              <P>Requests can be sent to: <Mail /></P>
              <P>We may need to verify the request before taking action.</P>
            </Sec>

            <Sec n={12} title="Security">
              <P>We use reasonable technical and organisational measures intended to protect information handled through Adaptience™.</P>
              <P>However, no online service or electronic storage system can guarantee absolute security.</P>
            </Sec>

            <Sec n={13} title="International Processing">
              <P>Technology providers used by Adaptience™ may process information in countries other than the country where the user is located.</P>
              <P>Where applicable, we will seek to handle such processing in accordance with relevant data-protection requirements.</P>
            </Sec>

            <Sec n={14} title="Early-Stage Service">
              <P>Adaptience™ is currently at an early stage of development.</P>
              <P>We may change how simulations, accounts, data storage and other functionality operate as the platform develops.</P>
              <P>If those changes materially affect our privacy practices, this Privacy Policy will be updated.</P>
            </Sec>

            <Sec n={15} title="Changes to This Privacy Policy">
              <P>We may update this Privacy Policy as Adaptience™ develops or as legal and regulatory requirements change.</P>
              <P>The latest version will be published on this website with an updated effective date.</P>
            </Sec>

            <Sec n={16} title="Contact">
              <P>For questions, privacy requests or concerns:</P>
              <P>Adaptience™</P>
              <P>Email: <Mail /></P>
            </Sec>
          </div>
          {/* =================== END PRIVACY POLICY CONTENT =================== */}

          <div className="mt-16 border-t border-white/[0.08] pt-10">
            <button
              onClick={onBack}
              data-testid="privacy-back-bottom-btn"
              className="inline-flex items-center gap-2 rounded-full px-7 py-3.5 text-sm font-semibold uppercase tracking-[0.16em] text-white transition-transform active:scale-[0.98]"
              style={{ background: ACCENT }}
            >
              <ArrowLeft size={15} />
              Back to Adaptience<TM />
            </button>
          </div>
        </motion.div>
      </main>
    </div>
  );
};

export default PrivacyPolicy;
